import React, { lazy, memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  BarChart2,
  Users,
  Search,
  FileText,
  Send,
  ChevronDown,
  Crosshair,
  ClipboardList,
  List,
  Info,
  Activity,
  Trophy,
  Palette,
  Reply,
  CircleDotIcon,
  TableOfContents,
  Component,
  ThumbsUp,
  Tractor,
  Pencil,
  FileBadge,
  FileCheck,
  LayoutList,
  Banknote,
  CalendarClock,
  Book,
  User,
  SendHorizonal,
  Award,
  Target,
  FileStack,
  ReplyIcon,
  RefreshCw,
  StickyNote,
  FileBarChart,
  Handshake,
} from 'lucide-react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useNavigate } from 'react-router-dom';

import { getAdminId, getPermissions, getRoles } from '../../utils/tokenUtils';
import SimpleAlert from '../common/SimpleAlert';
import { useCompetitionApi } from '../../hooks/useCompetitionApi';
import { CompetitionDTO } from '../../services/apiClient';

import 'react-perfect-scrollbar/dist/css/styles.css';

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  route?: string;
  requiredPermission?: string;
  children?: MenuItem[]; // Sub-items
}

interface MenuGroup {
  group: string;
  items: MenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  activePage: string;
}

const Sidebar: React.FC<SidebarProps> = memo(({ isOpen, activePage }) => {
  // Add ref for the PerfectScrollbar container
  const scrollContainerRef = React.useRef<any>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const roles = useMemo(() => getRoles() || [], []);
  const permissions = getPermissions() || [];
  const navigate = useNavigate();
  const adminId = getAdminId();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [activeCompetitionYear, setActiveCompetitionYear] = useState<number | null>(null);
  const { search: searchCompetitions } = useCompetitionApi();

  useEffect(() => {
    if (!roles.includes('User') || roles.includes('Admin')) return;
    const fetchActiveCompetition = async () => {
      try {
        const response = await searchCompetitions({ filters: {}, page: 1, pageSize: 100, sortBy: 'StartDate', sortDescending: false });
        const now = new Date();
        const active = ((response?.data?.results ?? []) as CompetitionDTO[]).find((c) => {
          const showFrom = new Date(c.showBannerDate ?? '');
          const regOpen = new Date(c.registrationOpenDate ?? '');
          const endDate = new Date(c.endDate ?? '');
          return showFrom <= now && regOpen <= now && now <= endDate;
        }) ?? null;
        setActiveCompetitionYear(active ? new Date(active.startDate ?? Date.now()).getFullYear() : null);
      } catch { }
    };
    fetchActiveCompetition();
  }, [roles]);

  // Memoize the base menuGroups to prevent recreation on every render
  const menuGroups = useMemo(
    () => [
      ...(roles.includes('Admin')
        ? [
          {
            group: 'Main',
            items: [
              {
                label: 'Dashboard',
                icon: <BarChart2 className="w-5 h-5" />,
                route: '/admin/dashboard',
                requiredPermission: 'Main.Dashboard.View',
              },
              {
                label: 'Notes / Tasks',
                icon: <StickyNote className="w-5 h-5" />,
                requiredPermission: 'Main.Note.View',
                children: [
                  {
                    label: 'Notes',
                    route: '/admin/notes',
                    requiredPermission: 'Main.Note.View',
                  },
                  {
                    label: 'Tasks',
                    route: '/admin/tasks',
                    requiredPermission: 'Main.Note.View',
                  },
                  {
                    label: 'My Tasks',
                    route: '/admin/my-tasks',
                    requiredPermission: 'Main.Note.View',
                  },
                  {
                    label: 'Flags',
                    route: '/admin/flags',
                    requiredPermission: 'Main.Note.View',
                  },
                ],
              },
            ],
          },
          {
            group: 'Communication',
            items: [
              {
                label: 'Bulk Email',
                icon: <Send className="w-5 h-5" />,
                route: '/admin/bulkemails',
                requiredPermission: 'Communication.BulkEmails.View',
              },
            ],
          },
          {
            group: 'Manage',
            items: [
              {
                label: 'Members',
                icon: <Users className="w-5 h-5" />,
                requiredPermission: 'Manage.Members.View',
                children: [
                  {
                    label: 'All',
                    route: '/admin/members',
                    requiredPermission: 'Manage.Members.View',
                  },
                  {
                    label: 'Application',
                    route: '/admin/members/status/newregistration',
                    requiredPermission: 'Manage.Members.View',
                  },
                  {
                    label: 'Active',
                    route: '/admin/members/status/active',
                    requiredPermission: 'Manage.Members.View',
                  },
                  {
                    label: 'No Status',
                    route: '/admin/members/status/none',
                    requiredPermission: 'Manage.Members.View',
                  },
                  {
                    label: 'Archived',
                    route: '/admin/members/status/archived',
                    requiredPermission: 'Manage.Members.View',
                  },
                  {
                    label: 'Deleted',
                    route: '/admin/members/status/deleted',
                    requiredPermission: 'Manage.Members.View',
                  },
                ],
              },
              {
                label: 'Scores',
                icon: <Target className="w-5 h-5" />,
                requiredPermission: 'Manage.Score.View',
                children: [
                  {
                    label: 'All',
                    route: '/admin/scores',
                    requiredPermission: 'Manage.Score.View',
                  },
                  {
                    label: 'In-House',
                    route: '/admin/scores/in-house',
                    requiredPermission: 'Manage.Score.View',
                  },
                  {
                    label: 'Competitions',
                    route: '/admin/scores/competition',
                    requiredPermission: 'Manage.Score.View',
                  },
                  {
                    label: 'Deleted',
                    route: '/admin/scores/deleted',
                    requiredPermission: 'Manage.Score.View',
                  },
                ],
              },
              {
                label: 'Activities',
                icon: <List className="w-5 h-5" />,
                requiredPermission: 'Manage.Activity.View',
                children: [
                  {
                    label: 'All',
                    route: '/admin/activities',
                    requiredPermission: 'Manage.Activity.View',
                  },
                  {
                    label: 'Deleted',
                    route: '/admin/activities/status/deleted',
                    requiredPermission: 'Manage.Activity.View',
                  },
                ],
              },
              {
                label: 'Endorsements',
                icon: <ThumbsUp className="w-5 h-5" />,
                requiredPermission: 'Manage.MemberEndorsement.View',
                children: [
                  {
                    label: 'All',
                    route: '/admin/memberendorsements',
                    requiredPermission: 'Manage.MemberEndorsement.View',
                  },
                  {
                    label: 'New',
                    route: '/admin/memberendorsements/status/new',
                    requiredPermission: 'Manage.MemberEndorsement.View',
                  },
                  {
                    label: 'Renewal',
                    route: '/admin/memberendorsements/status/renewal',
                    requiredPermission: 'Manage.MemberEndorsement.View',
                  },
                  {
                    label: 'Deleted',
                    route: '/admin/memberendorsements/status/deleted',
                    requiredPermission: 'Manage.MemberEndorsement.View',
                  },
                ],
              },
              {
                label: 'Courses',
                icon: <Award className="w-5 h-5" />,
                requiredPermission: 'Manage.Course.View',
                children: [
                  {
                    label: 'All',
                    route: '/admin/courses',
                    requiredPermission: 'Manage.Course.View',
                  },
                  {
                    label: 'Practical Evaluation',
                    route: '/admin/scores/evaluation',
                    requiredPermission: 'Manage.Score.View',
                  },
                  {
                    label: 'Printed',
                    route: '/admin/courses/status/print',
                    requiredPermission: 'Manage.Course.View',
                  },
                  {
                    label: 'Downloaded',
                    route: '/admin/courses/status/downloaded',
                    requiredPermission: 'Manage.Course.View',
                  },
                  {
                    label: 'Sport',
                    route: '/admin/courses/status/sport',
                    requiredPermission: 'Manage.Course.View',
                  },
                  {
                    label: 'Hunt',
                    route: '/admin/courses/status/hunt',
                    requiredPermission: 'Manage.Course.View',
                  },
                  {
                    label: 'Hunt & Sport',
                    route: '/admin/courses/status/huntsport',
                    requiredPermission: 'Manage.Course.View',
                  },
                  {
                    label: 'Deleted',
                    route: '/admin/courses/status/deleted',
                    requiredPermission: 'Manage.Course.View',
                  },
                ],
              },
              {
                label: 'Rpl',
                icon: <Award className="w-5 h-5" />,
                requiredPermission: 'Manage.Rpl.View',
                children: [
                  {
                    label: 'All',
                    route: '/admin/rpls',
                    requiredPermission: 'Manage.Rpl.View',
                  },
                  {
                    label: 'Sport',
                    route: '/admin/rpls/status/sport',
                    requiredPermission: 'Manage.Rpl.View',
                  },
                  {
                    label: 'Hunt',
                    route: '/admin/rpls/status/hunt',
                    requiredPermission: 'Manage.Rpl.View',
                  },
                  {
                    label: 'Hunt & Sport',
                    route: '/admin/rpls/status/huntsport',
                    requiredPermission: 'Manage.Rpl.View',
                  },
                  {
                    label: 'Deleted',
                    route: '/admin/rpls/status/deleted',
                    requiredPermission: 'Manage.Rpl.View',
                  },
                ],
              },
              {
                label: 'Competencies',
                icon: <FileBadge className="w-5 h-5" />,
                route: '/admin/membercompetencies',
                requiredPermission: 'Manage.MemberCompetency.View',
              },
              {
                label: 'Firearms',
                icon: <Crosshair className="w-5 h-5" />,
                route: '/admin/memberfirearms',
                requiredPermission: 'Manage.MemberFirearm.View',
              },
              {
                label: 'Competitions',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/competitions',
                requiredPermission: 'Manage.Competition.View',
                children: [
                  {
                    label: 'List',
                    route: '/admin/competitions',
                    requiredPermission: 'Manage.Competition.View',
                  },
                  {
                    label: 'Entries',
                    route: '/admin/competitionentries',
                    requiredPermission: 'Manage.CompetitionEntry.View',
                  },
                  {
                    label: 'Results',
                    route: '/admin/competitionresults',
                    requiredPermission: 'Manage.CompetitionResult.View',
                  },
                ],
              },
              {
                label: 'Ranges',
                icon: <CircleDotIcon className="w-5 h-5" />,
                route: '/admin/ranges',
                requiredPermission: 'Manage.Range.View',
              },
              {
                label: 'Listings',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/listings',
                requiredPermission: 'Manage.Listing.View',
              },

              {
                label: 'Content',
                icon: <TableOfContents className="w-5 h-5" />,
                requiredPermission: 'Manage.Members.View',
                children: [
                  {
                    label: 'Exam Questions',
                    route: '/admin/questionsets',
                    requiredPermission: 'Content.QuestionSet.View',
                  },
                  {
                    label: 'Natshoot Newsletters',
                    route: '/admin/newsletters',
                    requiredPermission: 'Content.Newsletter.View',
                  },
                  {
                    label: 'Info Hub Pages',
                    route: '/admin/pages',
                    requiredPermission: 'Content.Page.View',
                  },
                  {
                    label: "Articles / Blogs",
                    icon: <FileText className="w-5 h-5" />,
                    route: '/admin/articles',
                    requiredPermission: 'Content.Article.View',
                  },

                  {
                    label: "Current News",
                    icon: <FileText className="w-5 h-5" />,
                    route: '/admin/newsitems',
                    requiredPermission: 'Content.NewsItem.View',
                  },
                ],
              },
              {
                label: "Enquiries",
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/enquiries',
                requiredPermission: 'Manage.Enquiry.View',
              },
              {
                label: 'Message Replies',
                icon: <ReplyIcon className="w-5 h-5" />,
                requiredPermission: 'Manage.EmailMessageThread.View',
                children: [
                  {
                    label: 'All',
                    route: '/admin/replies',
                    requiredPermission: 'Manage.EmailMessageThread.View',
                  },
                  {
                    label: 'Members',
                    route: '/admin/replies/component/members',
                    requiredPermission: 'Manage.EmailMessageThread.View',
                  },
                  {
                    label: 'Scores',
                    route: '/admin/replies/component/scores',
                    requiredPermission: 'Manage.EmailMessageThread.View',
                  },
                  {
                    label: 'Activities',
                    route: '/admin/replies/component/activities',
                    requiredPermission: 'Manage.EmailMessageThread.View',
                  },
                  {
                    label: 'Endorsements',
                    route: '/admin/replies/component/endorsements',
                    requiredPermission: 'Manage.EmailMessageThread.View',
                  },
                  {
                    label: 'Dedicated Courses',
                    route: '/admin/replies/component/dedicatedcourses',
                    requiredPermission: 'Manage.EmailMessageThread.View',
                  },
                  {
                    label: 'RPL',
                    route: '/admin/replies/component/rpl',
                    requiredPermission: 'Manage.EmailMessageThread.View',
                  },
                ],
              },
            ],
          },
          {
            group: 'Finance',
            items: [
              {
                label: 'Renewals',
                icon: <RefreshCw className="w-5 h-5" />,
                route: '/admin/invoicebatches',
                requiredPermission: 'Finance.InvoiceBatch.View',
              },
              {
                label: 'Invoices',
                icon: <SendHorizonal className="w-5 h-5" />,
                route: '/admin/invoices',
                requiredPermission: 'Finance.Invoice.View',
              },
              {
                label: 'Recons',
                icon: <FileText className="w-5 h-5" />,
                requiredPermission: 'Finance.ReconTransaction.View',
                children: [
                  {
                    label: 'Transactions',
                    route: '/admin/recontransactions',
                    requiredPermission: 'Finance.ReconTransaction.View',
                  },
                  {
                    label: 'Confirmations',
                    route: '/admin/recontransactions/confirmations',
                    requiredPermission: 'Finance.ReconTransaction.View',
                  },
                  {
                    label: 'Unallocated',
                    route: '/admin/recontransactions/unallocated',
                    requiredPermission: 'Finance.ReconTransaction.View',
                  },
                ],
              },
            ],
          },
          {
            group: 'Reports',
            items: [
              {
                label: 'Finance',
                icon: <FileBarChart className="w-5 h-5" />,
                requiredPermission: 'Reports.Finance.View',
                children: [
                  {
                    label: 'Renewals',
                    route: '/admin/reports/finance/renewals',
                    requiredPermission: 'Reports.Finance.View',
                  },
                  {
                    label: 'Invoice',
                    route: '/admin/reports/finance/invoices',
                    requiredPermission: 'Reports.Finance.View',
                  },
                ],
              },
              {
                label: 'Course',
                icon: <Book className="w-5 h-5" />,
                requiredPermission: 'Reports.Courses.View',
                children: [
                  {
                    label: 'Duplicate Examinee ID',
                    route: '/admin/reports/courses/duplicate-examinee',
                    requiredPermission: 'Reports.Courses.View',
                  },
                ],
              },
              {
                label: 'Life Member',
                icon: <Users className="w-5 h-5" />,
                requiredPermission: 'Reports.LifeMembers.View',
                children: [
                  {
                    label: 'New Life Members',
                    route: '/admin/reports/members/life-members',
                    requiredPermission: 'Reports.LifeMembers.View',
                  },
                ],
              },
            ],
          },
          {
            group: 'Settings',
            items: [
              {
                label: 'Users',
                icon: <Users className="w-5 h-5" />,
                route: '/admin/users',
                requiredPermission: 'Settings.Administrators.View',
              },
              {
                label: 'Activities',
                icon: <Activity className="w-5 h-5" />,
                children: [
                  {
                    label: 'Type',
                    route: '/admin/activitytypes',
                    requiredPermission: 'Settings.ActivityType.View',
                  },

                  {
                    label: 'Category',
                    route: '/admin/activitycategories',
                    requiredPermission: 'Settings.ActivityCategory.View',
                  },
                  {
                    label: 'Species',
                    route: '/admin/activityspeciestype',
                    requiredPermission: 'Settings.ActivitySpeciesType.View',
                  },
                ],
              },
              {
                label: 'Audit Logs',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/auditlogs',
                requiredPermission: 'Settings.AuditLog.View',
              },
              {
                label: 'Colour Gradings',
                icon: <Palette className="w-5 h-5" />,
                route: '/admin/colourgradings',
                requiredPermission: 'Settings.ColourGrading.View',
              },
              {
                label: 'Company Info',
                icon: <Info className="w-5 h-5" />,
                route: '/admin/companyinfo',
                requiredPermission: 'Settings.CompanyInfo.View',
              },
              {
                label: 'VAT Rates',
                icon: <Banknote className="w-5 h-5" />,
                route: '/admin/vatratehistories',
                requiredPermission: 'Settings.VatRateHistory.View',
              },
              {
                label: 'Competition',
                icon: <Trophy className="w-5 h-5" />,
                children: [
                  {
                    label: 'Competition Classes',
                    route: '/admin/competitionclasses',
                    requiredPermission: 'Settings.CompetitionClass.View',
                  },
                  {
                    label: 'Competition Class Types',
                    route: '/admin/competitionclasstypes',
                    requiredPermission: 'Settings.CompetitionClassType.View',
                  },
                  {
                    label: 'Competition Exercise Type',
                    route: '/admin/competitionexercisetypes',
                    requiredPermission: 'Settings.CompetitionExerciseType.View',
                  },
                  {
                    label: 'Competition Statuses',
                    route: '/admin/competitionstatuses',
                    requiredPermission: 'Settings.CompetitionStatus.View',
                  },
                ],
              },
              {
                label: 'Component',
                icon: <Component className="w-5 h-5" />,
                children: [
                  {
                    label: 'Components',
                    route: '/admin/components',
                    requiredPermission: 'Settings.Component.View',
                  },
                  {
                    label: 'Component Actions',
                    route: '/admin/componentactions',
                    requiredPermission: 'Settings.ComponentAction.View',
                  },
                ],
              },
              {
                label: "Content Categories",
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/contentcategories',
                requiredPermission: 'Settings.ContentCategory.View',
              },
              {
                label: 'Course',
                icon: <Book className="w-5 h-5" />,
                children: [
                  {
                    label: 'Course Material Types',
                    route: '/admin/coursematerialtypes',
                    requiredPermission: 'Settings.CourseMaterialType.View',
                  },
                  {
                    label: 'Course Statuses',
                    route: '/admin/coursestatuses',
                    requiredPermission: 'Settings.CourseStatus.View',
                  },
                  {
                    label: 'Course Types',
                    route: '/admin/coursetypes',
                    requiredPermission: 'Settings.CourseType.View',
                  },
                ],
              },
              {
                label: 'Endorsement',
                icon: <ThumbsUp className="w-5 h-5" />,
                children: [
                  {
                    label: 'Endorsement Licence Types',
                    route: '/admin/endorsementlicencetypes',
                    requiredPermission: 'Settings.EndorsementLicenceType.View',
                  },
                  {
                    label: 'Endorsement Motivations',
                    route: '/admin/endorsementmotivations',
                    requiredPermission: 'Settings.EndorsementMotivation.View',
                  },
                  {
                    label: 'Endorsement Statuses',
                    route: '/admin/endorsementstatuses',
                    requiredPermission: 'Settings.EndorsementStatus.View',
                  },
                ],
              },
              {
                label: 'Exercises',
                icon: <ClipboardList className="w-5 h-5" />,
                children: [
                  {
                    label: 'Type',
                    route: '/admin/exercisetypes',
                    requiredPermission: 'Settings.ExerciseType.View',
                  },
                  {
                    label: 'Specific',
                    route: '/admin/exercisespecifics',
                    requiredPermission: 'Settings.ExerciseSpecific.View',
                  },
                  {
                    label: 'Targets',
                    route: '/admin/exercisetargets',
                    requiredPermission: 'Settings.ExerciseTarget.View',
                  },
                  {
                    label: 'Activity Targets',
                    route: '/admin/exerciseactivitytargets',
                    requiredPermission: 'Settings.ExerciseActivityTarget.View',
                  },
                ],
              },
              {
                label: 'Farm',
                icon: <Tractor className="w-5 h-5" />,
                children: [
                  {
                    label: 'Farm Statuses',
                    route: '/admin/farmstatuses',
                    requiredPermission: 'Settings.FarmStatus.View',
                  },
                  {
                    label: 'Farm Types',
                    route: '/admin/farmtypes',
                    requiredPermission: 'Settings.FarmType.View',
                  },
                ],
              },
              {
                label: 'Fellows',
                icon: <Handshake className="w-5 h-5" />,
                route: '/admin/fellows',
                requiredPermission: 'Manage.Fellow.View',
              },
              {
                label: 'Firearms',
                icon: <Crosshair className="w-5 h-5" />,
                children: [
                  {
                    label: 'Firearm Types',
                    route: '/admin/firearmtypes',
                    requiredPermission: 'Settings.FirearmType.View',
                  },
                  {
                    label: 'Firearm Specifics',
                    route: '/admin/firearmspecifics',
                    requiredPermission: 'Settings.FirearmSpecific.View',
                  },

                  {
                    label: 'Make (Firearm)',
                    route: '/admin/firearmmakes',
                    requiredPermission: 'Settings.FirearmMake.View',
                  },
                  {
                    label: 'Make (Barrel)',
                    route: '/admin/firearmbarrelmakes',
                    requiredPermission: 'Settings.FirearmBarrelMake.View',
                  },
                  {
                    label: 'Calibre',
                    route: '/admin/firearmcalibres',
                    requiredPermission: 'Settings.FirearmCalibre.View',
                  },
                  {
                    label: 'Barrel',
                    route: '/admin/firearmbarrels',
                    requiredPermission: 'Settings.FirearmBarrel.View',
                  },

                  {
                    label: 'Action',
                    route: '/admin/firearmactions',
                    requiredPermission: 'Settings.FirearmAction.View',
                  },
                  {
                    label: 'Purpose',
                    route: '/admin/firearmpurposes',
                    requiredPermission: 'Settings.FirearmPurpose.View',
                  },
                  {
                    label: 'Loose Barrel Types',
                    route: '/admin/firearmloosebarreltypes',
                    requiredPermission: 'Settings.FirearmLooseBarrelType.View',
                  },
                  {
                    label: 'Competency Types',
                    route: '/admin/firearmcompetencytypes',
                    requiredPermission: 'Settings.FirearmCompetencyType.View',
                  },
                  {
                    label: 'Serial Types',
                    route: '/admin/firearmserialtypes',
                    requiredPermission: 'Settings.FirearmSerialType.View',
                  },
                  {
                    label: 'Firearm Owners',
                    route: '/admin/firearmowners',
                    requiredPermission: 'Settings.FirearmOwner.View',
                  },
                ],
              },
              {
                label: 'Friends & Partners',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/friendandpartners',
                requiredPermission: 'Settings.FriendAndPartner.View',
              },
              {
                label: 'Gradings',
                icon: <Pencil className="w-5 h-5" />,
                children: [
                  {
                    label: 'Type',
                    route: '/admin/gradingtypes',
                    requiredPermission: 'Settings.GradingType.View',
                  },
                  {
                    label: 'Category',
                    route: '/admin/gradingcategories',
                    requiredPermission: 'Settings.GradingCategory.View',
                  },
                ],
              },
              {
                label: 'Invoice',
                icon: <FileCheck className="w-5 h-5" />,
                children: [
                  {
                    label: 'Invoice Statuses',
                    route: '/admin/invoicestatuses',
                    requiredPermission: 'Settings.InvoiceStatus.View',
                  },
                  {
                    label: 'Invoice Types',
                    route: '/admin/invoicetypes',
                    requiredPermission: 'Settings.InvoiceType.View',
                  },
                ],
              },
              {
                label: 'Listing',
                icon: <LayoutList className="w-5 h-5" />,
                children: [
                  {
                    label: 'Listing Statuses',
                    route: '/admin/listingstatuses',
                    requiredPermission: 'Settings.ListingStatus.View',
                  },
                  {
                    label: 'Listing Types',
                    route: '/admin/listingtypes',
                    requiredPermission: 'Settings.ListingType.View',
                  },
                ],
              },
              {
                label: 'Lookups',
                icon: <List className="w-5 h-5" />,
                children: [
                  {
                    label: 'Activity Statuses',
                    route: '/admin/activitystatuses',
                    requiredPermission: 'Settings.ActivityStatus.View',
                  },
                  {
                    label: 'Certificate Types',
                    route: '/admin/certificatetypes',
                    requiredPermission: 'Settings.CertificateType.View',
                  },
                  {
                    label: 'Competency Types',
                    route: '/admin/competencytypes',
                    requiredPermission: 'Settings.CompetencyType.View',
                  },

                  {
                    label: 'Countries',
                    route: '/admin/countries',
                    requiredPermission: 'Settings.Country.View',
                  },

                  {
                    label: 'Document Types',
                    route: '/admin/documenttypes',
                    requiredPermission: 'Settings.DocumentType.View',
                  },
                  {
                    label: 'Email Types',
                    route: '/admin/emailtypes',
                    requiredPermission: 'Settings.EmailType.View',
                  },

                  {
                    label: 'Enquiry Types',
                    route: '/admin/enquirytypes',
                    requiredPermission: 'Settings.EnquiryType.View',
                  },
                  {
                    label: 'Evaluation Statuses',
                    route: '/admin/evaluationstatuses',
                    requiredPermission: 'Settings.EvaluationStatus.View',
                  },
                  {
                    label: 'Exercise Templates',
                    route: '/admin/exercisetemplates',
                    requiredPermission: 'Settings.ExerciseTemplate.View',
                  },
                  {
                    label: 'Forfeiture Reasons',
                    route: '/admin/forfeiturereasons',
                    requiredPermission: 'Settings.ForfeitureReason.View',
                  },
                  {
                    label: 'Genders',
                    route: '/admin/genders',
                    requiredPermission: 'Settings.Gender.View',
                  },
                  {
                    label: 'Languages',
                    route: '/admin/languages',
                    requiredPermission: 'Settings.Language.View',
                  },
                  {
                    label: 'Message Template Statuses',
                    route: '/admin/messagetemplatestatuses',
                    requiredPermission: 'Settings.MessageTemplateStatus.View',
                  },
                  {
                    label: 'Module Types',
                    route: '/admin/moduletypes',
                    requiredPermission: 'Settings.ModuleType.View',
                  },
                  {
                    label: 'Newsletter Categories',
                    route: '/admin/newslettercategories',
                    requiredPermission: 'Settings.NewsletterCategory.View',
                  },
                  {
                    label: 'Note Types',
                    route: '/admin/notetypes',
                    requiredPermission: 'Settings.NoteType.View',
                  },
                  {
                    label: 'Payment Intervals',
                    route: '/admin/paymentintervals',
                    requiredPermission: 'Settings.PaymentInterval.View',
                  },
                  {
                    label: 'Payment Types',
                    route: '/admin/paymenttypes',
                    requiredPermission: 'Settings.PaymentType.View',
                  },
                  {
                    label: 'Provinces',
                    route: '/admin/provinces',
                    requiredPermission: 'Settings.Province.View',
                  },
                  {
                    label: 'Recon Types',
                    route: '/admin/recontypes',
                    requiredPermission: 'Settings.ReconType.View',
                  },
                  {
                    label: 'Renewal Statuses',
                    route: '/admin/renewalstatuses',
                    requiredPermission: 'Settings.RenewalStatus.View',
                  },
                  {
                    label: 'Reply Types',
                    route: '/admin/replytypes',
                    requiredPermission: 'Settings.ReplyType.View',
                  },
                  {
                    label: 'Rpl Statuses',
                    route: '/admin/rplstatuses',
                    requiredPermission: 'Settings.RplStatus.View',
                  },
                ],
              },
              {
                label: 'Member',
                icon: <Users className="w-5 h-5" />,
                children: [
                  {
                    label: 'Member Cancel Reasons',
                    route: '/admin/membercancelreasons',
                    requiredPermission: 'Settings.MemberCancelReason.View',
                  },
                  {
                    label: 'Member Categories',
                    route: '/admin/membercategories',
                    requiredPermission: 'Settings.MemberCategory.View',
                  },
                  {
                    label: 'Member Disciplinary Statuses',
                    route: '/admin/memberdisciplinarystatuses',
                    requiredPermission: 'Settings.MemberDisciplinaryStatus.View',
                  },
                  {
                    label: "Member Fees",
                    route: '/admin/memberfees',
                    requiredPermission: 'Settings.MemberFee.View',
                  },
                  {
                    label: 'Member Id Types',
                    route: '/admin/memberidtypes',
                    requiredPermission: 'Settings.MemberIdType.View',
                  },
                  {
                    label: 'Member Referral Types',
                    route: '/admin/memberreferraltypes',
                    requiredPermission: 'Settings.MemberReferralType.View',
                  },
                  {
                    label: 'Member Registration Steps',
                    route: '/admin/memberregistrationsteps',
                    requiredPermission: 'Settings.MemberRegistrationStep.View',
                  },
                  {
                    label: 'Member Registration Types',
                    route: '/admin/memberregistrationtypes',
                    requiredPermission: 'Settings.MemberRegistrationType.View',
                  },
                  {
                    label: 'Member Statuses',
                    route: '/admin/memberstatuses',
                    requiredPermission: 'Settings.MemberStatus.View',
                  },
                  {
                    label: 'Member Types',
                    route: '/admin/membertypes',
                    requiredPermission: 'Settings.MemberType.View',
                  },
                  {
                    label: 'Member Termination Statuses',
                    route: '/admin/memberterminationstatuses',
                    requiredPermission: 'Settings.MemberTerminationStatus.View',
                  },
                  {
                    label: 'Member Titles',
                    route: '/admin/membertitles',
                    requiredPermission: 'Settings.MemberTitle.View',
                  },
                ],
              },
              {
                label: 'Payment',
                icon: <Banknote className="w-5 h-5" />,
                children: [
                  {
                    label: 'Payment Intervals',
                    route: '/admin/paymentintervals',
                    requiredPermission: 'Settings.PaymentInterval.View',
                  },
                  {
                    label: 'Payment Types',
                    route: '/admin/paymenttypes',
                    requiredPermission: 'Settings.PaymentType.View',
                  },
                ],
              },
              {
                label: 'Quick Events',
                icon: <CalendarClock className="w-5 h-5" />,
                route: '/admin/quickevents',
                requiredPermission: 'Settings.QuickEvent.View',
              },
              {
                label: 'Range',
                icon: <CircleDotIcon className="w-5 h-5" />,
                children: [
                  {
                    label: 'Range Shooting Reasons',
                    route: '/admin/rangeshootingreasons',
                    requiredPermission: 'Settings.RangeShootingReason.View',
                  },
                  {
                    label: 'Range Statuses',
                    route: '/admin/rangestatuses',
                    requiredPermission: 'Settings.RangeStatus.View',
                  },
                  {
                    label: 'Range Types',
                    route: '/admin/rangetypes',
                    requiredPermission: 'Settings.RangeType.View',
                  },
                ],
              },
              {
                label: 'Reply Templates',
                icon: <Reply className="w-5 h-5" />,
                route: '/admin/replytemplates',
                requiredPermission: 'Settings.ReplyTemplate.View',
              },
              {
                label: 'Score',
                icon: <FileBadge className="w-5 h-5" />,
                children: [
                  {
                    label: 'Score Statuses',
                    route: '/admin/scorestatuses',
                    requiredPermission: 'Settings.ScoreStatus.View',
                  },
                  {
                    label: 'Score Types',
                    route: '/admin/scoretypes',
                    requiredPermission: 'Settings.ScoreType.View',
                  },
                ],
              },

            ],
          },
        ]
        : [
          {
            group: 'Main',
            items: [
              {
                label: 'Dashboard',
                icon: <BarChart2 className="w-5 h-5" />,
                route: '/admin/dashboard',
                requiredPermission: 'Main.Dashboard.View',
              },
              {
                label: 'Documents',
                icon: <FileStack className="w-5 h-5" />,
                route: '/admin/documents',
                requiredPermission: 'Main.Documents.View',
              },
              {
                label: 'Info Hub',
                icon: <Info className="w-5 h-5" />,
                route: '/content',
                requiredPermission: 'Main.InfoHub.View',
              },
            ],
          },
          {
            group: 'Manage',
            items: [
              {
                label: 'Firearm Compliance',
                icon: <CircleDotIcon className="w-5 h-5" />,
                children: [
                  {
                    label: 'My Firearms',
                    route: '/admin/memberfirearms',
                    requiredPermission: 'Manage.MemberFirearm.View',
                  },
                  {
                    label: 'My Competencies',
                    route: '/admin/membercompetencies',
                    requiredPermission: 'Manage.MemberCompetency.View',
                  },
                  {
                    label: 'Scores',
                    route: '/admin/scores',
                    requiredPermission: 'Manage.Score.View',
                  },
                  {
                    label: 'Activities',
                    route: '/admin/activities',
                    requiredPermission: 'Manage.Activity.View',
                  },
                  {
                    label: 'My Endorsements',
                    route: '/admin/memberendorsements',
                    requiredPermission: 'Manage.MemberEndorsement.View',
                  },
                ],
              },
              {
                label: 'Dedicated Status',
                icon: <FileBadge className="w-5 h-5" />,
                route: '/admin/dedicatedstatus',
                requiredPermission: 'Manage.DedicatedStatus.View',
              },
              ...(activeCompetitionYear ? [{
                label: `${activeCompetitionYear} Competition`,
                icon: <Trophy className="w-5 h-5" />,
                route: '/admin/competitionentries/create',
                requiredPermission: 'Manage.CompetitionEntry.View',
              }] : []),
            ],
          },
          {
            group: 'Setup',
            items: [
              {
                label: 'Member Setup',
                icon: <User className="w-5 h-5" />,
                route: '/admin/membersetup',
                requiredPermission: 'Main.MemberSetup.View',
              },
            ],
          },
        ]),
    ],
    [roles, activeCompetitionYear],
  );

  const handleNavigation = (page: string): void => {
    navigate(page);
  };

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label],
    );
  };

  const isRouteActive = (route: string) => {
    // Return false if either path is missing
    if (!activePage || !route) {
      return false;
    }

    const currentPath = activePage.toLowerCase();
    const menuRoute = route.toLowerCase();

    // Exact match
    if (currentPath === menuRoute) {
      return true;
    }

    // Special handling for member status routes
    if (menuRoute === '/admin/members' && currentPath.includes('/admin/members/status/')) {
      return false; // Don't highlight "All" when viewing a status-specific page
    }

    // Special handling for recon transaction sub-routes
    if (menuRoute === '/admin/recontransactions' && currentPath.startsWith('/admin/recontransactions/')) {
      return false; // Don't highlight "Transactions" when viewing filtered recon pages
    }

    // Special handling for endorsement status routes
    if (
      menuRoute === '/admin/memberendorsements' &&
      currentPath.includes('/admin/memberendorsements/status/')
    ) {
      return false; // Don't highlight "All" when viewing a status-specific page
    }

    // Special handling for email message thread component routes
    if (menuRoute === '/admin/replies' && currentPath.includes('/admin/replies/component/')) {
      return false; // Don't highlight "All" when viewing a component-specific page
    }

    // For other member routes (like edit, create)
    if (menuRoute === '/admin/members' && currentPath.startsWith('/admin/members/')) {
      return !currentPath.includes('/status/'); // Only highlight "All" for non-status routes
    }

    // For other endorsement routes (like edit, create)
    if (
      menuRoute === '/admin/memberendorsements' &&
      currentPath.startsWith('/admin/memberendorsements/')
    ) {
      return !currentPath.includes('/status/'); // Only highlight "All" for non-status routes
    }

    if (menuRoute === '/admin/activities' && currentPath.startsWith('/admin/activities/')) {
      return !currentPath.includes('/status/'); // Only highlight "All" for non-status routes
    }

    if (menuRoute === '/admin/rpls' && currentPath.startsWith('/admin/rpls/')) {
      return !currentPath.includes('/status/'); // Only highlight "All" for non-status routes
    }

    if (menuRoute === '/admin/courses' && currentPath.startsWith('/admin/courses/')) {
      return !currentPath.includes('/status/'); // Only highlight "All" for non-status routes
    }

    // For other reply routes (like edit, create)
    if (menuRoute === '/admin/replies' && currentPath.startsWith('/admin/replies/')) {
      return !currentPath.includes('/component/'); // Only highlight "All" for non-component routes
    }

    if (menuRoute === '/admin/scores' && currentPath.startsWith('/admin/scores/')) {
      const statusTypes = ['evaluation', 'competition', 'in-house', 'deleted'];
      return !statusTypes.some((type) => currentPath === `/admin/scores/${type}`);
    }

    // Check other sub-routes
    return currentPath.startsWith(menuRoute + '/');
  };

  // 2. Memoize the renderMenuItems function
  const renderMenuItems = useCallback(
    (items: MenuItem[]) => {
      return items.map((item, idx) => {
        const isParentActive = item.children
          ? item.children.some((child) => child.route && isRouteActive(child.route))
          : item.route && isRouteActive(item.route);

        const isExpanded = expandedItems.includes(item.label) || isParentActive;

        return (
          <li key={idx}>
            <a
              onClick={() => {
                if (item.children) {
                  toggleExpand(item.label);
                } else if (item.route) {
                  handleNavigation(item.route);
                }
              }}
              className={`flex items-center gap-3 p-3 rounded transition text-sm cursor-pointer ${isParentActive
                ? 'bg-white text-primary border-l-4 border-secondary dark:bg-primary dark:text-white'
                : 'hover:bg-white hover:text-primary text-white dark:hover:bg-primary dark:hover:text-white'
                }`}
            >
              {item.icon}
              <span className="flex-1">{item.label}</span>
              {item.children && (
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              )}
            </a>
            {item.children && (
              <ul
                className={`relative ml-6 overflow-hidden transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-auto mt-1' : 'max-h-0'
                  }`}
              >
                <div className="absolute top-0 bottom-0 w-px bg-gray-600"></div>
                {item.children.map((child, childIdx) => (
                  <li key={`${idx}-${childIdx}`} className="relative">
                    <div className="absolute left-0 top-1/2 w-4 h-px bg-gray-600"></div>
                    <a
                      onClick={() => child.route && handleNavigation(child.route)}
                      data-route={child.route} // Add this line
                      className={`flex items-center gap-3 py-2 pl-6 rounded transition text-sm cursor-pointer ${child.route && isRouteActive(child.route)
                        ? 'text-secondary'
                        : 'text-gray-400 hover:text-white'
                        }`}
                    >
                      <span>{child.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      });
    },
    [expandedItems, isRouteActive, handleNavigation],
  );

  // Move filterMenuItem above filteredGroups and memoize it
  const filterMenuItem = useMemo(() => {
    return (item: MenuItem): boolean => {
      // If the item has no permission requirement and no children, show it
      if (!item.requiredPermission && !item.children) {
        return true;
      }

      // If the item has a permission requirement, check it
      if (item.requiredPermission && !permissions.includes(item.requiredPermission)) {
        return false;
      }

      // If the item has children, check if any children are accessible
      if (item.children) {
        const accessibleChildren = item.children.filter(
          (child) => !child.requiredPermission || permissions.includes(child.requiredPermission),
        );
        return accessibleChildren.length > 0;
      }

      return true;
    };
  }, [permissions]);

  // 4. Optimize the filteredGroups memo
  const filteredGroups = useMemo(
    () =>
      menuGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            const mainItemMatchesSearch = searchTerm
              ? item.label.toLowerCase().includes(searchTerm.toLowerCase())
              : true;

            const childrenMatchSearch =
              searchTerm && item.children
                ? item.children.some((child) =>
                  child.label.toLowerCase().includes(searchTerm.toLowerCase()),
                )
                : false;

            const isAccessible = filterMenuItem(item);

            if (item.children) {
              item.children = item.children.filter(
                (child) =>
                  (!child.requiredPermission || permissions.includes(child.requiredPermission)) &&
                  (!searchTerm || // Only filter by search if there is a search term
                    child.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    mainItemMatchesSearch),
              );
            }

            return (mainItemMatchesSearch || childrenMatchSearch) && isAccessible;
          }),
        }))
        .filter((group) => group.items.length > 0),
    [menuGroups, permissions, searchTerm, filterMenuItem],
  );

  // 5. Add a stable reference for the scroll handler
  const handleScroll = useCallback((e: any) => {
    if (scrollContainerRef.current?._ps) {
      localStorage.setItem(
        'sidebarScrollPosition',
        scrollContainerRef.current._ps.element.scrollTop.toString(),
      );
    }
  }, []);

  // Replace the useEffect with useLayoutEffect
  useLayoutEffect(() => {
    if (!activePage) return;

    // Shorter timeout and immediate scroll attempt
    const immediateScroll = () => {
      if (scrollContainerRef.current?._ps) {
        const savedPosition = localStorage.getItem('sidebarScrollPosition');
        const container = scrollContainerRef.current._ps.element;

        if (savedPosition) {
          container.scrollTop = parseInt(savedPosition, 10);
        } else {
          const activeElement = document.querySelector(
            `[data-route="${activePage}"]`,
          ) as HTMLElement;

          if (activeElement) {
            const containerHeight = container.clientHeight;
            const elementTop = activeElement.offsetTop;
            container.scrollTop = elementTop - containerHeight / 2;
          }
        }
        scrollContainerRef.current._ps.update();
      }
    };

    immediateScroll();
    // Backup timeout in case immediate scroll fails
    const timer = setTimeout(immediateScroll, 0);

    return () => clearTimeout(timer);
  }, [activePage]);

  return (
    <aside
      id="nav-menu"
      aria-label="Side navigation"
      className={`fixed lg:static z-40 w-72 bg-gray-800 border-r border-gray-700 h-[calc(100%-4rem)] lg:h-full transition-transform ${isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 mt-12 lg:mt-0 flex flex-col`}
    >
      {/* Search Box */}
      <div className="relative my-6 px-4">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search here"
          aria-label="Search content"
          className="relative w-full h-10 px-4 pr-12 text-sm transition-all border rounded outline-none 
            border-gray-600 bg-gray-800 text-gray-300 autofill:bg-gray-800 
            focus:border-secondary focus:outline-none 
            disabled:cursor-not-allowed disabled:bg-gray-700 disabled:text-gray-500"
        />
        <Search
          className="absolute top-2.5 right-7 h-5 w-5 cursor-pointer stroke-gray-300"
          aria-hidden="true"
          aria-label="Search icon"
          role="graphics-symbol"
        />
      </div>

      {/* Navigation Links */}
      <PerfectScrollbar
        ref={scrollContainerRef}
        options={{ suppressScrollX: true }}
        className="flex-1 overflow-hidden"
        onScrollY={handleScroll}
      >
        <div className="">
          {filteredGroups.map((group, index) => (
            <div key={index} className="mb-4">
              <h2 className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
                {group.group}
              </h2>
              <ul className="space-y-2 px-4">{renderMenuItems(group.items)}</ul>
            </div>
          ))}
        </div>
      </PerfectScrollbar>
      {adminId && <SimpleAlert message="Logged in as member" variant="success" />}

      {/* Footer */}
      <footer className="border-t border-gray-700 p-4 mt-auto">
        <p className="text-sm text-gray-400">
          &copy; {new Date().getFullYear()} Natshoot. All rights reserved. Developed by Latreia.
        </p>
      </footer>
    </aside>
  );
});

// Add display name for debugging
Sidebar.displayName = 'Sidebar';

// 6. Memoize the entire Sidebar component
export default Sidebar;
